import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Menu,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { canvasVersionsAPI, adminAgentsAPI } from '../../../services/api';
import { hasSubAgentNodes } from './promptUtils';

export default function CanvasVersionBar({ agentName, canvasNodes, canvasEdges, hasDraft, onDraftSaved, canUndo, undoRef, discardDraftRef, reloadRef, versionsRef, onDiffRequested }) {
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [labelError, setLabelError] = useState('');
  const [activating, setActivating] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [includeSubAgents, setIncludeSubAgents] = useState(false);
  const [exportError, setExportError] = useState(null);
  const importInputRef = useRef(null);
  const LABEL_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;

  const load = useCallback(() => {
    if (!agentName) return;
    canvasVersionsAPI
      .list(agentName)
      .then((d) => {
        const list = d.versions || [];
        setVersions(list);
        if (versionsRef) versionsRef.current = list;
      })
      .catch(() => {});
  }, [agentName, versionsRef]);

  useEffect(() => {
    load();
  }, [load]);

  const active = versions.find((v) => v.is_active);

  const handleExportClick = () => {
    if (hasSubAgentNodes(canvasNodes)) {
      setIncludeSubAgents(false);
      setExportDialogOpen(true);
    } else {
      _doExport(false);
    }
  };

  const _doExport = async (withSubAgents) => {
    setExportDialogOpen(false);
    setExportError(null);
    try {
      const bundle = await adminAgentsAPI.exportAgent(agentName, withSubAgents);
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${agentName}.agent.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Export failed. Please try again.');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    setImportError(null);
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      const result = await adminAgentsAPI.importAgentBundle(bundle);
      reloadRef?.current?.();
      // Re-importing the current agent navigates to the same route, so the bar does not
      // remount — refetch the version history + active flag explicitly, or they stay stale.
      load();
      navigate(`/admin/agent-builder/${encodeURIComponent(result.agent)}`);
    } catch {
      setImportError('Failed to import agent. Make sure the file is a valid agent export.');
    } finally {
      setImporting(false);
    }
  };

  const clearDraft = () => {
    try { localStorage.removeItem(`canvas_draft_${agentName}`); } catch { /* ignore */ }
    onDraftSaved?.();
  };

  const handleSave = async () => {
    if (labelError) return;
    setSaving(true);
    try {
      await adminAgentsAPI.saveCanvasConfig(agentName, canvasNodes, canvasEdges);
      const version = await canvasVersionsAPI.create(agentName, label);
      await canvasVersionsAPI.activate(agentName, version.id);
      clearDraft();
      setLabel('');
      setLabelError('');
      setDialogOpen(false);
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Save failed';
      if (status === 409) setLabelError(detail);
      else if (status === 400) setLabelError(detail);
      else setLabelError(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (versionId) => {
    if (compareMode) {
      setAnchorEl(null);
      setCompareMode(false);
      onDiffRequested?.(versionId);
      return;
    }
    setAnchorEl(null);
    setActivating(true);
    try {
      await canvasVersionsAPI.activate(agentName, versionId);
      setVersions((prev) => prev.map((v) => ({ ...v, is_active: v.id === versionId })));
      clearDraft();
      reloadRef?.current?.();
    } finally {
      load();
      setActivating(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {activating
          ? 'Switching version…'
          : active ? `Active: ${active.label || active.created_at?.slice(0, 16) || active.id}` : 'No active version'}
        {activating && <CircularProgress size={10} />}
      </Typography>
      {hasDraft && (
        <Typography variant="caption" sx={{ color: 'warning.main', fontSize: 10, fontStyle: 'italic' }}>
          · Draft
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      <Button
        size="small"
        variant="outlined"
        sx={{ fontSize: 10, py: 0.25 }}
        onClick={() => undoRef?.current?.()}
        disabled={!canUndo}
        title="Undo (Ctrl/Cmd+Z)"
      >
        Undo
      </Button>
      {hasDraft && (
        <Button
          size="small"
          variant="outlined"
          color="warning"
          sx={{ fontSize: 10, py: 0.25 }}
          onClick={() => setDiscardOpen(true)}
        >
          Discard Draft
        </Button>
      )}
      <Button
        size="small"
        variant="outlined"
        sx={{ fontSize: 10, py: 0.25 }}
        onClick={() => importInputRef.current.click()}
        disabled={importing}
      >
        {importing ? 'Importing…' : 'Import'}
      </Button>
      <input
        ref={importInputRef}
        type="file"
        accept=".json,application/json"
        style={{ display: 'none' }}
        onChange={handleImport}
      />
      <Button
        size="small"
        variant="outlined"
        sx={{ fontSize: 10, py: 0.25 }}
        onClick={handleExportClick}
        disabled={!agentName}
      >
        Export
      </Button>
      <Button
        size="small"
        variant="outlined"
        sx={{ fontSize: 10, py: 0.25 }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={activating}
      >
        History ({versions.length})
      </Button>
      <Button
        size="small"
        variant="contained"
        sx={{ fontSize: 10, py: 0.25 }}
        onClick={() => setDialogOpen(true)}
      >
        Save Version
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => { setAnchorEl(null); setCompareMode(false); }}
        PaperProps={{ sx: { minWidth: 260 } }}
      >
        {versions.length === 0 && (
          <MenuItem disabled sx={{ fontSize: 11 }}>
            No versions yet
          </MenuItem>
        )}
        {[...versions].reverse().map((v) => (
          <MenuItem
            key={v.id}
            onClick={() => handleActivate(v.id)}
            sx={{
              fontSize: 11,
              fontWeight: v.is_active ? 'bold' : 'normal',
              ...(compareMode && {
                cursor: 'crosshair',
                '&:hover': { bgcolor: 'rgba(124,58,237,0.1)' },
              }),
            }}
          >
            {v.label || v.created_at?.slice(0, 16) || v.id} {v.is_active ? '✓' : ''}
          </MenuItem>
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.75, pb: 0.25 }}>
          {compareMode ? (
            <Box
              onClick={() => setCompareMode(false)}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                bgcolor: '#f3e8ff', color: '#7c3aed', fontWeight: 600, fontSize: 10.5,
                borderRadius: 999, px: 1.5, py: 0.5,
                '&:hover': { bgcolor: '#e9d5ff' },
              }}
            >
              Pick a version above to diff against… ✕
            </Box>
          ) : (
            <Box
              onClick={() => setCompareMode(true)}
              sx={{
                display: 'inline-flex', alignItems: 'center', cursor: 'pointer',
                bgcolor: '#f3e8ff', color: '#7c3aed', fontWeight: 600, fontSize: 11,
                borderRadius: 999, px: 2, py: 0.5,
                '&:hover': { bgcolor: '#e9d5ff' },
              }}
            >
              Diff
            </Box>
          )}
        </Box>
      </Menu>

      <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 13 }}>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12 }}>
            This will permanently delete your local draft and reload the canvas from the server.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setDiscardOpen(false)}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            onClick={() => {
              discardDraftRef?.current?.();
              onDraftSaved?.();
              setDiscardOpen(false);
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!importError} onClose={() => setImportError(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 13 }}>Import failed</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 12 }}>{importError}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setImportError(null)}>OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 13 }}>Export Agent</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeSubAgents}
                onChange={(e) => setIncludeSubAgents(e.target.checked)}
                size="small"
              />
            }
            label={<Typography sx={{ fontSize: 12 }}>Include sub-agents</Typography>}
          />
          {includeSubAgents && (
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5, ml: 3.5 }}>
              Sub-agent canvases are included for AI-assisted editing. Re-importing this bundle will not recreate sub-agent registrations.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button size="small" variant="contained" onClick={() => _doExport(includeSubAgents)}>
            Export
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!exportError} onClose={() => setExportError(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 13 }}>Export failed</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 12 }}>{exportError}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setExportError(null)}>OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 13 }}>Save Canvas Version</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Label (optional)"
            size="small"
            fullWidth
            value={label}
            onChange={(e) => {
              const v = e.target.value;
              setLabel(v);
              if (!v) {
                setLabelError('');
              } else if (!LABEL_REGEX.test(v)) {
                setLabelError('Lowercase letters, digits, single dashes only (e.g. version-1)');
              } else {
                setLabelError('');
              }
            }}
            error={!!labelError}
            helperText={labelError || 'Leave blank to auto-name as version-N'}
            sx={{ mt: 1 }}
            inputProps={{ style: { fontSize: 11 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave} disabled={saving || !!labelError}>
            {saving ? <CircularProgress size={14} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
